/**
 * Строка навигации слева сверху вместо надписи «prokat» — как `NavCommand` в supply.
 * Телефон → карточка звонков номера `/calls/phone/<номер>` (привязка номера + звонки);
 * `з 4366330` — звонок, `л 937186` — лид, `с 1252456` или просто число — сделка; остальное — «Поиск».
 * `/` вне поля ввода — сюда, Esc — выйти. Спека `docs/specs/2026-10-20-call-route-design.md`.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { normPhone } from "@/lib/callRoute";

const HINTS: [string, string][] = [
  ["+7 908 139-10-81", "звонки номера и куда он привязан"],
  ["з 4366330", "звонок: кому дозванивались"],
  ["с 1252456", "сделка (или просто номер)"],
  ["л 937186", "лид"],
  ["что угодно", "поиск"],
];

const PREFIX: [RegExp, string][] = [
  [/^(з|звонок|call)\s*(\d+)$/i, "/calls/"],
  [/^(л|лид|lead)\s*(\d+)$/i, "/leads/"],
  [/^(с|сделка|deal)\s*(\d+)$/i, "/deals/"],
];

/** Текст строки → адрес экрана. */
export function target(text: string): string {
  const q = text.trim();
  for (const [re, path] of PREFIX) {
    const m = q.match(re);
    if (m) return `${path}${Number(m[2])}`;
  }
  if (/^\d{1,8}$/.test(q)) return `/deals/${Number(q)}`;
  const phone = /^[\d\s()+-]+$/.test(q) ? normPhone(q) : "";
  if (phone) return `/calls/phone/${phone}`;
  return `/search?q=${encodeURIComponent(q)}`;
}

function editable(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);
}

export function NavCommand() {
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [text, setText] = useState("");
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey || editable(document.activeElement)) return;
      e.preventDefault();
      input.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = () => {
    if (!text.trim()) return;
    void navigate(target(text));
    setText("");
    input.current?.blur();
  };

  return (
    <div className="relative min-w-0 flex-1" data-annot="строка навигации">
      <input
        ref={input}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => {
          if (e.key === "Enter") go();
          if (e.key === "Escape") {
            setText("");
            input.current?.blur();
          }
        }}
        placeholder="телефон, з/с/л номер"
        title="Быстрая навигация. Enter — перейти, / — сюда, Esc — выйти"
        aria-label="Быстрая навигация"
        spellCheck={false}
        autoComplete="off"
        className="h-[26px] w-full rounded-[var(--radius-ui)] border border-border bg-bg px-2 font-mono text-[12.5px] outline-none placeholder:text-fg-faint focus:border-fg-muted"
      />
      {focused && !text.trim() && (
        <div className="absolute left-0 top-[30px] z-50 w-[300px] rounded-[var(--radius-ui)] border border-border bg-bg p-2 text-[12px] shadow-lg">
          <table className="w-full">
            <tbody>
              {HINTS.map(([cmd, what]) => (
                <tr key={cmd}>
                  <td className="whitespace-nowrap py-0.5 pr-3 font-mono text-fg">{cmd}</td>
                  <td className="py-0.5 text-fg-muted">{what}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
